import type { Query, ResultSet } from '@cubejs-client/core'
import {
  hashKey,
  keepPreviousData,
  useQuery,
  useQueryClient,
  type QueryKey,
} from '@tanstack/react-query'
import { useEffect, useEffectEvent } from 'react'

import { cubeApi, getCubeLiveApi } from '@/lib/cube'

interface CubeLiveQueryOptions<T> {
  /** `queryKeys.cube(view, …)` — anahtar `query`'yi tam olarak belirlemeli. */
  queryKey: QueryKey
  /** `buildCubeQuery()`'den geçmiş sorgu (sözleşme §1.4). */
  query: Query
  select: (result: ResultSet) => T
  /** false: sorgu HTTP ile bir kez yüklenir, WS aboneliği açılmaz (şablon önizlemesi). */
  subscribe?: boolean
  /** false: sorguyu tamamen kapatır (yükleme de abonelik de). */
  enabled?: boolean
  /**
   * Cube'un `compact` yanıt biçimi: üye adları her satırda tekrarlanmaz, büyük ham
   * satır sorgularında yük yarıya iner. Sayılar dizgi gelir (istemcinin `castNumerics`'i
   * bu biçimde çalışmıyor, 1.7.42): `select` sayısal alanları kendisi çevirmeli.
   */
  compact?: boolean
  /**
   * Sorgunun `limit`'i dolarsa sonraki sayfalar `offset` ile çekilir (en çok `maxRows`).
   * Sorgu kararlı bir `order` taşımalı; `select` dizi döndürmeli. Canlı abonelik yalnız
   * ilk sayfayı izler (canlı aralıklar küçük).
   */
  paginate?: { maxRows: number }
  /** Arka planda sessiz yenileme aralığı (ms); sekme görünür değilken durur. */
  refetchInterval?: number | false
  /** Ekrandan ayrıldıktan sonra sonucun önbellekte kalma süresi (ms). */
  gcTime?: number
}

/**
 * Canlı Cube sorgusu (bkz. docs/CUBE-MIGRATION-PLAN.md G6.4). Canlı sorgu
 * yalnızca bu hook ile açılır.
 *
 * İlk yükleme, hata ve yeniden deneme TanStack Query'nin normal yolunda HTTP ile
 * yapılır. Yanında bir WS aboneliği açılır; Cube sonucu değiştiğinde gönderir,
 * gelen sonuç aynı anahtarla önbelleğe yazılır (sunucu durumu yalnızca TanStack
 * Query'de, AGENTS.md §3). Abonelik hatası (ör. süresi dolmuş token) mevcut veriyi
 * ezmez.
 */
export function useCubeLiveQuery<T>({
  queryKey,
  query,
  select,
  enabled = true,
  subscribe = true,
  compact = false,
  paginate,
  refetchInterval,
  gcTime,
}: CubeLiveQueryOptions<T>) {
  const queryClient = useQueryClient()
  const format = compact ? 'compact' : 'default'
  const options = compact ? { castNumerics: false } : {}
  const load = (page: Query) => cubeApi.load(page, options, undefined, format)

  const loadPages = async (maxRows: number): Promise<T> => {
    const size = query.limit ?? maxRows
    let rows: unknown[] = []
    for (let offset = 0; rows.length < maxRows; offset += size) {
      const page = select(await load({ ...query, offset })) as unknown[]
      rows = rows.concat(page)
      if (page.length < size) break
    }
    return rows as T
  }

  const result = useQuery({
    queryKey,
    queryFn: async () => (paginate ? loadPages(paginate.maxRows) : select(await load(query))),
    enabled,
    refetchInterval,
    ...(gcTime !== undefined && { gcTime }),
    // Filtre/sayfa değişince önceki veri ekranda kalır (`isPlaceholderData`); rapordan
    // rapora geçişte taşınmaz, `ReportPage` `key={report.view}` ile yeniden mount eder.
    placeholderData: keepPreviousData,
  })

  // Anahtar değişmedikçe yeniden abone olunmaz; her render'da yeni nesne olarak
  // gelen `query`/`select` son hâlleriyle okunur.
  const onResult = useEffectEvent((error: Error | null, resultSet: ResultSet | null) => {
    if (error || !resultSet) {
      console.warn('Cube canlı abonelik hatası', error)
      return
    }
    queryClient.setQueryData(queryKey, select(resultSet))
  })
  const currentQuery = useEffectEvent(() => query)
  const keyHash = hashKey(queryKey)

  useEffect(() => {
    if (!enabled || !subscribe) return
    const subscription = getCubeLiveApi().subscribe(
      currentQuery(),
      options,
      (error, resultSet) => onResult(error, resultSet),
      format,
    )
    return () => {
      void subscription.unsubscribe()
    }
    // `options`/`format` yalnız `compact`'tan türer.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, subscribe, compact, keyHash])

  return result
}
